import { User, UserManager, UserManagerSettings } from "oidc-client";
import { createUserManager } from "redux-oidc";
import { defaultUserManagerSettings, oidcClientId } from "../Constants/AuthConfiguration";

import { endpoints } from "../Constants/endpoints";

interface IAuthService {
  isAuthenticated: () => Promise<boolean>;
  getUser: () => Promise<User | null>;
  getUserManager: () => Promise<UserManager>;
  getToken: () => Promise<string | undefined>;
  signIn: () => void;
  signOut: () => void;
}

class AuthService implements IAuthService {
  userManager: UserManager | undefined;

  async isAuthenticated() {
    return (await this.getUser()) !== null;
  }

  async getUser() {
    const userManager = await this.getUserManager();
    return userManager.getUser();
  }

  async getUserManager() {
    await this.ensureUserManagerInitialized();
    return this.userManager as UserManager;
  }

  async getToken() {
    const user = await this.getUser();
    return user ? user.access_token : undefined;
  }

  async signIn() {
    const userManager = await this.getUserManager();

    // // First try to silently authenticate the user
    try {
      await userManager.signinSilent();
      return;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("Failed to sign in silently.", error);
    }

    // Next try redirecting user to sign in page
    try {
      await userManager.signinRedirect({
        state: JSON.stringify({ postLoginRedirectPage: window.location.pathname })
      });
      return;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("Failed to sign in via redirect", error);
    }

    throw new Error("Failed to sign in user");
  }

  async signOut() {
    const userManager = await this.getUserManager();

    // First try to logout via redirect
    try {
      await userManager.signoutRedirect();
      return;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("Failed to sign out via redirect");
    }

    // Next just try dropping local user data
    try {
      await userManager.removeUser();
      await userManager.revokeAccessToken();
      return;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("Failed to sign out by dropping user");
    }

    throw new Error("Failed to sign out user");
  }

  async ensureUserManagerInitialized() {
    if (this.userManager) return;

    const response = await fetch(endpoints.oidcConfiguration(oidcClientId));
    if (!response.ok)
      throw new Error(
        `Failed to get the OIDC-configuration from server: Server returned ${response.status}`
      );

    const serverOidcSettings: UserManagerSettings = await response.json();

    const userManagerSettings = {
      ...serverOidcSettings,
      ...defaultUserManagerSettings
    };
    this.userManager = createUserManager(userManagerSettings);
    this.userManager.events.addAccessTokenExpired(async () => {
      // @ts-ignore
      await this.signinSilent(this.userManager);
    });

    this.userManager.events.addAccessTokenExpiring(async () => {
      // @ts-ignore
      await this.signinSilent(this.userManager);
    });
  }

  async signinSilent(userManager: UserManager) {
    userManager.signinSilent().catch(async err => {
      await this.signOut();
    });
  }
}

export const authService = new AuthService();
